import React, { useState } from 'react';
import { X, RotateCcw, AlertCircle } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';

import { RubyGemMark } from '../RubyGemMark';
interface Props {
  payment: { transactionId: string; amount: number; refundedAmount?: number };
  onClose: () => void;
  onRefunded: () => void;
}

const RefundModal: React.FC<Props> = ({ payment, onClose, onRefunded }) => {
  const maxRefundable = Math.max(0, payment.amount - (payment.refundedAmount || 0));
  const [amount, setAmount] = useState(String(maxRefundable.toFixed(2)));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountNum = Number(amount);
  const isValid = !isNaN(amountNum) && amountNum > 0 && amountNum <= maxRefundable && reason.trim().length >= 3;

  const handleRefund = async () => {
    if (!isValid) return;
    try {
      setBusy(true);
      setError(null);
      await api.posPayments.refund({
        transactionId: payment.transactionId,
        amount: amountNum,
        reason: reason.trim(),
      });
      toastService.success(`Refunded $${amountNum.toFixed(2)}`);
      onRefunded();
    } catch (err: any) {
      setError(err?.message || 'Refund failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="flex items-center gap-3 p-5 border-b border-stone-100">
          <div className="w-10 h-10 rounded-lg bg-stone-700 flex items-center justify-center shrink-0">
            <RotateCcw size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-stone-900 font-semibold text-base">Refund transaction</h2>
            <p className="text-stone-500 text-xs mt-0.5 truncate font-mono">{payment.transactionId}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-stone-500">Original</span>
              <span className="font-mono text-stone-800">${payment.amount.toFixed(2)}</span>
            </div>
            {payment.refundedAmount ? (
              <div className="flex justify-between mt-1">
                <span className="text-stone-500">Already refunded</span>
                <span className="font-mono text-stone-800">${payment.refundedAmount.toFixed(2)}</span>
              </div>
            ) : null}
            <div className="flex justify-between mt-1 pt-1 border-t border-stone-200">
              <span className="text-stone-700 font-medium">Max refundable</span>
              <span className="font-mono text-stone-800 font-medium">${maxRefundable.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1">Refund amount ($)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={maxRefundable}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1">Reason</label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Customer request, duplicate charge, etc."
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-stone-100 bg-stone-50 rounded-b-2xl flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm font-medium text-stone-600 hover:text-stone-800">Cancel</button>
          <button
            onClick={handleRefund}
            disabled={!isValid || busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-stone-700 text-white hover:bg-stone-800 disabled:opacity-50"
          >
            {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <RotateCcw size={14} />}
            {busy ? 'Refunding…' : `Refund $${(Number.isFinite(amountNum) ? amountNum : 0).toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
